var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { TextCell } from '@juvare/common-data-table-ui/DataTable';
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { MenuIconButton } from '@juvare/common-web-ui/MenuIconButton';
import { formatDateTime } from '@juvare/common-web-ui/utils';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { useFunctionClient } from '@juvare/platform-sdk-react/ui/hooks';
import { downloadFile } from '@juvare/platform-sdk-react/ui/utils';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Stack } from '@mui/material';
import React from 'react';
import { useSearchParams } from 'react-router';
import { fileVersionEntityName } from '../../backend/features/file-manager';
import { allowWrite, getDefaultPermissions } from '../../backend/features/permissions';
import { WatchButton, showArchiveDialog, showHistoryDialog, showMoveDialog } from '../components';
import { showCopyDialog } from '../components/dialogs/CopyDialog';
import { humanReadableFileSize, renderFileNameColumn, renderFileTypeColumn } from '../utils';
import { renderOriginalLocationColumn } from '../utils/renderOriginalLocationColumn';
import { useDocumentLibraryStorage } from './useDocumentLibraryStorage';
import { useDocumentLibraryTranslations } from './useDocumentLibraryTranslations';
import { useEditDialogMenuItem } from './useEditDialogMenuItem';
export const fileManagerRowMenuItemsIds = {
    openDirectory: 'open-directory',
    downloadFile: 'download-file',
    editDetails: 'edit-details',
    manageAccess: 'manage-access',
    fileHistory: 'file-history',
    archive: 'archive',
    move: 'move',
    copyFile: 'copy-file',
};
export function useFileColumns({ defaultPermissions, clearActiveRow, navigate, reload, onManagePermissions, isSearching, customRowMenuItems, }) {
    const { t } = useDocumentLibraryTranslations();
    const userState = useUserState();
    const [searchParams] = useSearchParams();
    const { fileStorageClient, copyFile } = useDocumentLibraryStorage();
    const functionClient = useFunctionClient();
    const editDialogMenuItem = useEditDialogMenuItem();
    const getMenuItems = React.useCallback((row) => {
        const menuItems = [];
        const defaultPermissions = getDefaultPermissions();
        const fileEntityType = row.columns.directory ? t('common.folder') : t('common.file');
        const canModify = row.permissions.includes('modify');
        if (row.columns.directory) {
            menuItems.push({
                label: t('documentPanel.open'),
                id: fileManagerRowMenuItemsIds.openDirectory,
                onClick: () => navigate(row.id),
                'data-test': 'menu-item-open',
            });
        }
        if (!row.columns.directory) {
            menuItems.push({
                label: t('documentPanel.download'),
                id: fileManagerRowMenuItemsIds.downloadFile,
                onClick: () => __awaiter(this, void 0, void 0, function* () {
                    if (row.columns.currentVersionId) {
                        yield downloadFile({
                            entity: fileVersionEntityName,
                            entityId: row.columns.currentVersionId,
                            key: 'data',
                            downloadFileName: row.columns.name,
                        });
                    }
                }),
                'data-test': 'menu-item-download',
            });
        }
        if (canModify) {
            menuItems.push(editDialogMenuItem({
                fileEntityType,
                fileColumns: row,
                onEditDetails: () => {
                    reload();
                },
            }));
            menuItems.push({
                label: t('documentPanel.manageAccess'),
                id: fileManagerRowMenuItemsIds.manageAccess,
                onClick: () => {
                    fileStorageClient
                        .get(row.id)
                        .then((entityData) => {
                        if (onManagePermissions && entityData) {
                            onManagePermissions(entityData, reload);
                        }
                    })
                        .catch(handleErrorDialog);
                },
                'data-test': 'menu-item-manage-access',
            });
            menuItems.push({
                label: t('documentPanel.move'),
                id: fileManagerRowMenuItemsIds.move,
                onClick: () => showMoveDialog({
                    storage: fileStorageClient,
                    t,
                    file: row,
                    showRootDirectory: allowWrite({
                        userState: userState,
                        ownerId: null,
                        permissions: defaultPermissions,
                        rootLevel: true,
                    }),
                    fileEntityType,
                    onSuccess: () => reload(),
                }),
                'data-test': 'menu-item-move',
            });
            if (!row.columns.directory) {
                menuItems.push({
                    label: t('documentPanel.history'),
                    id: fileManagerRowMenuItemsIds.fileHistory,
                    onClick: () => {
                        showHistoryDialog({
                            entityId: row.id,
                            fileName: row.columns.name,
                            currentVersionId: row.columns.currentVersionId,
                            onBackToFiles: () => reload(),
                        });
                    },
                    'data-test': 'menu-item-history',
                });
                menuItems.push({
                    label: t('documentPanel.copy'),
                    id: fileManagerRowMenuItemsIds.copyFile,
                    onClick: () => showCopyDialog({
                        copyFile,
                        t,
                        file: row,
                        showRootDirectory: allowWrite({
                            userState: userState,
                            ownerId: null,
                            permissions: defaultPermissions,
                            rootLevel: true,
                        }),
                        onSuccess: () => reload(),
                    }),
                    'data-test': 'menu-item-copy',
                });
            }
            menuItems.push({
                label: t('documentPanel.archive'),
                id: fileManagerRowMenuItemsIds.archive,
                onClick: () => showArchiveDialog({ t, functionClient, file: row, onSuccess: reload }),
                'data-test': 'menu-item-archive',
            });
        }
        return customRowMenuItems ? customRowMenuItems({ menuItems, row, reload }) : menuItems;
    }, [
        t,
        customRowMenuItems,
        reload,
        navigate,
        editDialogMenuItem,
        fileStorageClient,
        onManagePermissions,
        userState,
        copyFile,
        functionClient,
    ]);
    return React.useMemo(() => [
        {
            sortKey: 'name',
            headerLabel: t('documentPanel.name'),
            cellFunction: (row) => (React.createElement(TextCell, null,
                React.createElement(Stack, { alignItems: 'center', direction: 'row', gap: 1 },
                    renderFileTypeColumn(t, row, defaultPermissions, clearActiveRow, navigate, searchParams),
                    renderFileNameColumn(t, row, navigate, searchParams)))),
            tableCellHeadSx: {
                paddingLeft: 4,
            },
            tableCellSx: {
                width: {
                    xs: '65%',
                    sm: '80%',
                    md: '30%',
                },
            },
        },
        {
            sortKey: 'ownerDisplayName',
            headerLabel: t('documentPanel.owner'),
            cellFunction: (row) => React.createElement(TextCell, { sx: { wordBreak: 'break-word' } }, row.columns.ownerDisplayName),
            tableCellSx: {
                display: {
                    xs: 'none',
                    md: 'table-cell',
                },
                width: {
                    md: '20%',
                },
            },
        },
        {
            headerLabel: t('documentPanel.lastUpdated'),
            cellFunction: (row) => React.createElement(TextCell, null, formatDateTime(new Date(row.updated))),
            tableCellSx: {
                display: {
                    xs: 'none',
                    md: 'table-cell',
                },
                width: {
                    md: '20%',
                },
            },
        },
        {
            sortKey: 'contentLength',
            headerLabel: t('common.size'),
            cellFunction: (row) => row.columns.contentLength ? React.createElement(TextCell, null, humanReadableFileSize(row.columns.contentLength)) : null,
            tableCellSx: {
                display: {
                    xs: 'none',
                    md: 'table-cell',
                },
                width: {
                    md: '15%',
                },
            },
        },
        ...(isSearching ? [renderOriginalLocationColumn(t)] : []),
        {
            headerLabel: t('documentPanel.watch'),
            cellFunction: (row) => (React.createElement(WatchButton, { fileName: row.columns.name, fileId: row.id, watchersList: row.columns.watchersList, onReload: reload })),
            tableCellSx: {
                textAlign: 'end',
            },
        },
        {
            cellFunction: (row) => {
                const menuItems = getMenuItems(row);
                if (menuItems.length === 0) {
                    return null;
                }
                return (React.createElement(MenuIconButton, { menuItems: menuItems.map((item) => ({
                        label: item.label,
                        onClick: item.onClick,
                        'data-test': item['data-test'],
                    })), stopPropagationOnClick: true, "data-test": 'more-button', "aria-label": t('documentPanel.openMenu') },
                    React.createElement(MoreHorizIcon, null)));
            },
            tableCellSx: {
                width: 72,
            },
        },
    ], [t, clearActiveRow, defaultPermissions, getMenuItems, navigate, reload, searchParams, isSearching]);
}
